import { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  InvitationForm,
  InvitationFormData,
} from "@/app/components/invitation/InvitationForm";
import {
  RenterRequisiteForm,
  RenterRequisiteFormData,
} from "@/app/components/invitation/RenterRequisiteForm";
import { InvitationReview } from "@/app/components/invitation/InvitationReview";
import { Stepper } from "@/app/components/initiate/Stepper";
import { ModalError } from "@/app/components/modals/modalBuilder";
import { ConfirmDeleteModal } from "@/app/components/modals/service/confirmDeleteModal";
import { useDisclosure } from "@heroui/react";
import useErrorHandling from "@/hooks/useErrorHandling";
import { api } from "@/api";
import { normalizeDate } from "@/utils";
import { InvitationUpdate } from "@/api/invitations";
import { useAppSelector } from "@/hooks/useAppSelector";
import {
  OfficesSelectionForm,
  OfficesSelectionFormData,
} from "@/app/components/invitation/OfficesSelectionForm";

export function EditExistingInvitationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const handleError = useErrorHandling();
  const businessProfileId = useAppSelector(
    (state) => state.businessProfile.businessProfile.id,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [defaultValues, setDefaultValues] =
    useState<Partial<InvitationFormData>>();
  const [invitationData, setInvitationData] =
    useState<InvitationFormData | null>(null);
  const [defaultOfficesValues, setDefaultOfficesValues] =
    useState<OfficesSelectionFormData | null>(null);
  const [officesData, setOfficesData] =
    useState<OfficesSelectionFormData | null>(null);
  const [createNewRequisite, setCreateNewRequisite] = useState(false);
  const [defaultRenterRequisiteValues, setDefaultRenterRequisiteValues] =
    useState<Partial<RenterRequisiteFormData>>();
  const [renterRequisiteData, setRenterRequisiteData] =
    useState<RenterRequisiteFormData | null>(null);
  const [modalErrors, setModalErrors] = useState<ModalError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const deleteConfirmDisclosure = useDisclosure();

  const steps = useMemo(() => {
    const baseSteps = [
      {
        name: "Данные приглашения",
        description: "Заполните основную информацию",
        longDescription: "Укажите арендатора и параметры договора",
      },
      {
        name: "Офисы",
        description: "Выберите офисы и рабочие места",
        longDescription: "Укажите офисы и рабочие места для договора",
      },
    ];

    if (createNewRequisite) {
      baseSteps.push({
        name: "Реквизиты арендатора",
        description: "Заполните реквизиты арендатора",
        longDescription: "Введите реквизиты нового арендатора",
      });
    }

    baseSteps.push({
      name: "Проверка данных",
      description: "Проверьте введенные данные",
      longDescription: "Убедитесь, что все данные корректны перед отправкой",
    });

    return baseSteps;
  }, [createNewRequisite]);

  const completedSteps = useMemo(() => {
    const completed: number[] = [];
    if (invitationData || defaultValues) {
      completed.push(0);
    }
    if (officesData || defaultOfficesValues) {
      completed.push(1);
    }
    if (
      createNewRequisite &&
      (renterRequisiteData || defaultRenterRequisiteValues)
    ) {
      completed.push(2);
    }
    return completed;
  }, [
    invitationData,
    defaultValues,
    officesData,
    defaultOfficesValues,
    renterRequisiteData,
    defaultRenterRequisiteValues,
    createNewRequisite,
  ]);

  useEffect(() => {
    if (!id) return;

    const loadInvitation = async () => {
      try {
        const invitationResponse = await api.invitations.get(Number(id));
        const invitation = invitationResponse.data;

        if (invitation.type !== "existing_renter") {
          toast.error("Неправильный тип приглашения");
          navigate("/dashboard/invitations");
          return;
        }

        // Получаем данные договора для получения полной информации
        const agreementResponse = await api.agreements.get(
          invitation.agreement_id,
        );
        const agreement = agreementResponse;

        setDefaultValues({
          startDate: normalizeDate(
            invitation.start_date ?? agreement.startDate,
          ),
          endDate: normalizeDate(invitation.end_date ?? agreement.endDate),
          meetingHours: agreement.meetingHours,
          renter: agreement.renterId,
          renterRequisite: agreement.renterRequisiteId || undefined,
          requisite: agreement.requisiteId || undefined,
          type: "existing_renter",
          number: agreement.number,
        });

        // Загружаем офисы и рабочие места из договора
        const combinedOffices: Array<{
          office_id: number | null;
          workplace_id: number | null;
          price: number;
        }> = [
          ...agreement.offices.map((o) => ({
            office_id: o.officeId,
            price: o.price,
            workplace_id: null,
          })),
        ];

        setDefaultOfficesValues({
          offices: combinedOffices,
        });

        // Если есть meta_id, значит есть реквизиты - показываем шаг реквизитов
        if (invitation.meta_id) {
          setCreateNewRequisite(true);
          try {
            const metaResponse = await api.invitations.getMeta(
              invitation.meta_id,
            );
            const metaData = metaResponse.data;

            // Проверяем тип meta данных (по наличию requisite_name)
            if ("requisite_name" in metaData) {
              setDefaultRenterRequisiteValues({
                requisite_name: metaData.requisite_name,
                address: metaData.address,
                ogrn: metaData.ogrn,
                inn: metaData.inn,
                kpp: metaData.kpp,
                bik: metaData.bik,
                bank_account: metaData.bank_account,
                correspondent_account: metaData.correspondent_account,
                bank_name: metaData.bank_name,
                phone_number: metaData.phone_number,
                email: metaData.email,
              });
            } else {
              setDefaultRenterRequisiteValues({});
            }
          } catch (metaErr: any) {
            // Если не удалось загрузить meta данные, продолжаем без них
            console.error("Failed to load meta data:", metaErr);
            setDefaultRenterRequisiteValues({});
          }
        }

        setIsLoading(false);
      } catch (err: any) {
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
        navigate("/dashboard/invitations");
      }
    };

    loadInvitation();
  }, [id, navigate, handleError]);

  const handleCreateRequisiteToggle = useCallback((value: boolean) => {
    setCreateNewRequisite(value);
    if (!value) {
      setRenterRequisiteData(null);
    }
  }, []);

  const handleInvitationSubmit = useCallback((data: InvitationFormData) => {
    setInvitationData(data);
    setDefaultValues(data);
    setModalErrors([]);
    setActiveStep(1); // Переход к шагу выбора офисов
  }, []);

  const handleOfficesSubmit = useCallback(
    (data: OfficesSelectionFormData) => {
      setOfficesData(data);
      setDefaultOfficesValues(data);
      setModalErrors([]);
      if (createNewRequisite) {
        setActiveStep(2); // Переход к шагу реквизитов
      } else {
        setActiveStep(steps.length - 1); // Переход к проверке данных
      }
    },
    [createNewRequisite, steps.length],
  );

  const handleRenterRequisiteSubmit = useCallback(
    (data: RenterRequisiteFormData) => {
      setRenterRequisiteData(data);
      setDefaultRenterRequisiteValues(data);
      setModalErrors([]);
      setActiveStep(steps.length - 1); // Переход к проверке данных
    },
    [steps.length],
  );

  const handleReviewSubmit = useCallback(async () => {
    if (!invitationData || !id || !invitationData.renter || !officesData)
      return;

    setIsSubmitting(true);

    try {
      // Разделяем офисы и рабочие места
      const offices = officesData.offices
        .filter((item) => item.office_id !== null && !item.workplace_id)
        .map((item) => ({
          office_id: item.office_id!,
          price: item.price,
        }));

      const workplaces = officesData.offices
        .filter((item) => item.workplace_id !== null)
        .map((item) => ({
          workplace_id: item.workplace_id!,
          price: item.price,
        }));

      const payload: InvitationUpdate = {
        business_profile_id: businessProfileId,
        start_date: invitationData.startDate,
        end_date: invitationData.endDate,
        meetingHours: invitationData.meetingHours,
        offices: offices,
        workplaces: workplaces,
        renter: invitationData.renter,
        requisite: invitationData.requisite,
        type: "existing_renter",
        number: invitationData.number,
      };

      if (createNewRequisite && renterRequisiteData) {
        payload.meta = {
          requisite_name: renterRequisiteData.requisite_name,
          address: renterRequisiteData.address,
          ogrn: renterRequisiteData.ogrn,
          inn: renterRequisiteData.inn,
          kpp: renterRequisiteData.kpp,
          bik: renterRequisiteData.bik,
          bank_account: renterRequisiteData.bank_account,
          correspondent_account: renterRequisiteData.correspondent_account,
          bank_name: renterRequisiteData.bank_name,
          phone_number: renterRequisiteData.phone_number,
          email: renterRequisiteData.email,
        };
      }

      await api.invitations.update(Number(id), payload);

      toast.success("Приглашение успешно обновлено");
      setIsSubmitting(false);
      navigate("/dashboard/invitations");
    } catch (err: any) {
      const { errorMessage, parsedErrors } = handleError(err);
      setModalErrors(parsedErrors || []);
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  }, [
    invitationData,
    officesData,
    createNewRequisite,
    renterRequisiteData,
    id,
    businessProfileId,
    navigate,
    handleError,
  ]);

  const handleEdit = useCallback(() => {
    if (createNewRequisite && renterRequisiteData) {
      setActiveStep(2); // Редактирование реквизитов
    } else if (officesData || defaultOfficesValues) {
      setActiveStep(1); // Редактирование офисов
    } else {
      setActiveStep(0); // Редактирование данных приглашения
    }
  }, [
    createNewRequisite,
    renterRequisiteData,
    officesData,
    defaultOfficesValues,
  ]);

  const changeStep = useCallback((step: number) => {
    setActiveStep(step);
  }, []);

  const handleDelete = useCallback(() => {
    deleteConfirmDisclosure.onOpen();
  }, [deleteConfirmDisclosure]);

  const confirmDelete = useCallback(async () => {
    if (!id) return;

    try {
      await api.invitations.delete(Number(id));
      toast.success("Приглашение успешно удалено");
      navigate("/dashboard/invitations");
    } catch (err: any) {
      const { errorMessage } = handleError(err);
      toast.error(errorMessage);
    }
  }, [id, navigate, handleError]);

  if (isLoading) {
    return <div>Загрузка...</div>;
  }

  return (
    <>
      <ConfirmDeleteModal
        isOpen={deleteConfirmDisclosure.isOpen}
        onOpenChange={deleteConfirmDisclosure.onOpenChange}
        callback={confirmDelete}
      />

      <h1 className="text-xl font-bold mb-4 mt-2">
        Редактирование приглашения
      </h1>

      <div className="flex flex-col lg:flex-row gap-4 w-full">
        <div className="lg:w-1/3">
          <Stepper
            steps={steps}
            activeStep={activeStep}
            changeStep={changeStep}
            colorScheme="primary"
            completedSteps={completedSteps}
          />
        </div>

        <div className="lg:w-2/3">
          <div className="w-full h-full p-8 border-2 border-foreground-200 dark:border-foreground-100 rounded-xl bg-background">
            <div className="flex flex-col gap-4">
              {!(
                "hideTitle" in steps[activeStep] && steps[activeStep].hideTitle
              ) && (
                <div className="flex flex-col gap-2">
                  <span className="text-lg font-semibold">
                    {steps[activeStep].name}
                  </span>
                  <span className="text-sm text-gray-500">
                    {steps[activeStep].longDescription}
                  </span>
                </div>
              )}

              {activeStep === 0 && (
                <InvitationForm
                  type="existing_renter"
                  defaultValues={invitationData || defaultValues}
                  onSubmit={handleInvitationSubmit}
                  isEdit={true}
                  modalErrors={modalErrors}
                  onDelete={handleDelete}
                  createNewRequisite={createNewRequisite}
                  onCreateRequisiteToggle={handleCreateRequisiteToggle}
                />
              )}

              {activeStep === 1 && (
                <OfficesSelectionForm
                  defaultValues={
                    officesData || defaultOfficesValues || undefined
                  }
                  onSubmit={handleOfficesSubmit}
                  modalErrors={modalErrors}
                />
              )}

              {activeStep === 2 && createNewRequisite && (
                <RenterRequisiteForm
                  defaultValues={
                    renterRequisiteData || defaultRenterRequisiteValues
                  }
                  onSubmit={handleRenterRequisiteSubmit}
                  isEdit={true}
                  modalErrors={modalErrors}
                  onDelete={handleDelete}
                />
              )}

              {activeStep === steps.length - 1 && (
                <InvitationReview
                  invitationData={
                    invitationData || (defaultValues as InvitationFormData)
                  }
                  renterRequisiteData={renterRequisiteData || undefined}
                  officesData={officesData || defaultOfficesValues || undefined}
                  onEdit={handleEdit}
                  onSubmit={handleReviewSubmit}
                  onDelete={handleDelete}
                  isSubmitting={isSubmitting}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
